import React, { forwardRef, memo, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { MainSideBarNavItem } from '@components/NavItem/_NavItem'
import { SpaceNavItem } from '@components/NavItem/SpaceNavItem'
import { RegisterMainShortcuts } from '@components/Shortcuts/RegisterMainShortcuts'
import { Box, Card, Dot, Icon } from '@ui'
import { motion } from 'framer-motion'
import { Analytics } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useOrderedSpaces } from 'hooks/useFilteredSortedSpaces'
import { isEqual } from 'lodash-es'
import { PATHS } from 'routes'
import { useUserStore } from 'store/userSettingsStore'
import { vars } from 'ui/styles/vars.css'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    SpaceItem,
    useDmsAndGdms,
    useDmsAndGdmsUnreads,
    useMyUserId,
    useSpaceContext,
    useTownsContext,
} from 'use-towns-client'
import { isTownBanned } from 'utils'

const EMPTY_ARRAY: string[] = []

export const MainSideBar = () => {
    const { isTouch } = useDevice()
    const { spaces } = useTownsContext()
    const { spaceId } = useSpaceContext()
    const { dmsAndGdms } = useDmsAndGdms()
    const { dmsAndGdmsUnreadIds } = useDmsAndGdmsUnreads()

    const hasSpaces = spaces.length > 0
    const hasDms = dmsAndGdms.length > 0

    // Filter out banned towns from spaces list
    const filteredSpaces = useMemo(() => {
        return spaces.filter((space) => {
            const address = addressFromSpaceId(space.id)
            return address ? !isTownBanned(address) : true
        })
    }, [spaces])

    const onShowCreateSpace = useEvent(() => {
        Analytics.getInstance().track('clicked new town')
    })

    return (
        <Card hideBackground paddingTop="none">
            {!isTouch && <RegisterMainShortcuts />}
            {(hasSpaces || hasDms) && (
                <MainSideBarNavItem
                    to={`/${PATHS.MESSAGES}`}
                    tooltip="Direct Messages"
                    data-testid="direct-messages"
                >
                    <Box position="relative">
                        <Icon width="x4" aspectRatio="1/1" size="square_lg" type="dm" />
                        {dmsAndGdmsUnreadIds.size > 0 && <Dot position="topRight" />}
                    </Box>
                </MainSideBarNavItem>
            )}
            <TransitionItem key="explore">
                <MainSideBarNavItem
                    to={`/${PATHS.EXPLORE}`}
                    tooltip="Explore"
                    data-testid="explore"
                >
                    <Box position="relative">
                        <Icon type="explore" size="square_sm" color="gray2" />
                    </Box>
                </MainSideBarNavItem>
            </TransitionItem>
            <TransitionItem key="bots">
                <MainSideBarNavItem to={`/${PATHS.BOTS}`} tooltip="Bots" data-testid="bots">
                    <Box position="relative" insetTop="2">
                        <Icon type="robot" size="square_sm" color="botGradient" />
                    </Box>
                </MainSideBarNavItem>
            </TransitionItem>
            <TransitionItem key="new">
                <MainSideBarNavItem
                    to={`/${PATHS.SPACES}/new`}
                    tooltip="New Town"
                    data-testid="add-new-town"
                    onClick={onShowCreateSpace}
                >
                    <Box position="relative">
                        <Icon type="plus" size="square_sm" color="gray2" />
                    </Box>
                </MainSideBarNavItem>
            </TransitionItem>
            <Box paddingX="xs">
                <Box borderBottom="faint" />
            </Box>

            <Box grow position="relative">
                <Box scroll absoluteFill paddingTop="sm">
                    <SpaceList spaces={filteredSpaces} spaceId={spaceId} />
                </Box>

                <Box
                    position="topLeft"
                    height="x2"
                    pointerEvents="none"
                    style={{
                        backgroundImage: `linear-gradient(to top, transparent, ${vars.color.background.level1})`,
                    }}
                    width="100%"
                />
            </Box>
        </Card>
    )
}

export const SpaceList = memo(
    (props: {
        spaces: SpaceItem[]
        spaceId?: string
        onSelectSpace?: (spaceId: string) => void
    }) => {
        const { spaces, spaceId } = props
        const userId = useMyUserId()

        const favoritedSpaces = useUserStore(
            (s) => (userId && s.users[userId]?.favoriteSpaces) || EMPTY_ARRAY,
        )

        const spaceIdsRef = useRef<string[]>([])
        const spaceIds = useMemo(() => {
            const ids = spaces.map((s) => s.id)
            if (isEqual(ids, spaceIdsRef.current)) {
                return spaceIdsRef.current
            }
            spaceIdsRef.current = ids
            return ids
        }, [spaces])
        const orderedSpaces = useOrderedSpaces({ spaceIds, favoritedSpaces })

        const navigate = useNavigate()

        const onSelectItem = useCallback(
            (spaceId: string) => {
                navigate(
                    `/${PATHS.SPACES}/${addressFromSpaceId(spaceId)}/${PATHS.SPACE_DEFAULT_REDIRECT}`,
                )
                props.onSelectSpace?.(spaceId)
            },
            [navigate, props],
        )

        return orderedSpaces.map((s) => (
            <TransitionItem key={s}>
                <SpaceNavItem
                    selected={s === spaceId}
                    id={s}
                    spaceName={spaces.find((sp) => sp.id === s)?.name || ''}
                    onClick={() => {
                        onSelectItem(s)
                    }}
                />
            </TransitionItem>
        ))
    },
)
SpaceList.displayName = 'SpaceList'

const TransitionItem = memo(
    forwardRef<HTMLDivElement, { children: React.ReactNode }>((props, ref) => (
        <motion.div
            layout="position"
            ref={ref}
            transition={{
                restDelta: 0.01,
                type: 'spring',
                stiffness: 500,
                damping: 40,
                opacity: {
                    duration: 0.4,
                    delay: 0.1,
                },
                layout: {
                    type: 'spring',
                    stiffness: 500,
                    damping: 50,
                },
            }}
            initial="hide"
            animate="show"
            variants={{
                hide: { opacity: 0 },
                show: { opacity: 1 },
            }}
        >
            {props.children}
        </motion.div>
    )),
)
